import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import { Card, FormHelperText, MenuItem } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React, { useMemo } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDInput from "@/components/atoms/MDInput";
import MDTypography from "@/components/atoms/MDTypography";
import {
  getFreightOptionByValue,
  getFreightOverheadTypeOptions,
  getFreightTypeOptions,
} from "@/components/templates/CustomerForm/constants";
import { CustomerConfigurationFormData } from "@/components/templates/CustomerForm/types";
import {
  UNIT_DELIVERY_TIME_MAX_LENGTH,
} from "@/constants/unit-delivery-time.constants";
import useActiveLogisticsPlatforms from "@/hooks/data/use-active-logistics-platforms";
import useDialog from "@/hooks/use-dialog";
import { CustomerFormMessages } from "@/messages/customer/customer-form.messages";
import { FreightOverheadType, FreightType } from "@/types/customer-configuration.types";

import FreightDescriptionModal from "../FreightDescriptionModal";

const FREIGHT_INPUT_FONT = '400 1rem "Roboto","Helvetica","Arial",sans-serif';
const FREIGHT_FIELD_ADORNMENT_PX = 36;

function measureTextWidth(text: string, font: string): number {
  if (typeof document === "undefined") return text.length * 8;

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) return text.length * 8;

  context.font = font;
  return Math.ceil(context.measureText(text).width);
}

type Props = {
  messages: CustomerFormMessages;
};

function FreightCard({ messages }: Props) {
  const formik = useFormikContext<CustomerConfigurationFormData>();
  const { values, errors, setFieldValue } = formik;
  const { isVisible, handleOpen, handleClose } = useDialog();
  const {
    activeLogisticsPlatformLabel,
    isInitialLoading,
    hasLoadError,
  } = useActiveLogisticsPlatforms();
  const freightOptions = getFreightTypeOptions(messages);
  const isAutomatedFreight = Number(values.freightType) === FreightType.AutomatedFreightTable;
  const selectedFreightOption = getFreightOptionByValue(Number(values.freightType), messages);
  const freightOverheadTypeOptions = getFreightOverheadTypeOptions(messages);
  const isPercentageOverhead = values.freightOverheadType === FreightOverheadType.Percentage;

  const platformHint = (() => {
    if (isInitialLoading) return null;
    if (hasLoadError) return `(${messages.logisticsPlatformLoadErrorHint})`;
    if (activeLogisticsPlatformLabel) return `(${activeLogisticsPlatformLabel})`;
    return `(${messages.noLogisticsPlatformActiveHint})`;
  })();

  const freightFieldWidthPx = useMemo(() => {
    const maxTextWidth = freightOptions.reduce((maxWidth, option) => {
      const optionWidth = measureTextWidth(option.label, FREIGHT_INPUT_FONT);
      return Math.max(maxWidth, optionWidth);
    }, 0);

    return (maxTextWidth || 280) + FREIGHT_FIELD_ADORNMENT_PX;
  }, [freightOptions]);

  const freightInputWidthPx = freightFieldWidthPx - FREIGHT_FIELD_ADORNMENT_PX;

  const platformHintColor =
    hasLoadError || !activeLogisticsPlatformLabel ? "warning" : "secondary";

  return (
    <Card sx={{ height: "100%", overflow: "visible" }}>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between">
          <MDTypography variant="h5" fontWeight="bold">
            {messages.freightSection}
          </MDTypography>
        </MDBox>
        <MDBox mt={1}>
          <Grid container spacing={1.5}>
            <Grid size={12}>
              <MDBox
                display="flex"
                alignItems="flex-end"
                flexWrap="nowrap"
                columnGap={0.5}
                sx={{
                  width: "100%",
                  overflowX: "auto",
                  overflowY: "visible",
                  scrollbarWidth: "thin",
                }}
              >
                <Autocomplete
                  disableClearable
                  options={freightOptions}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  noOptionsText={messages.noDescriptionAvailable}
                  slotProps={{
                    popper: {
                      sx: { minWidth: freightFieldWidthPx },
                    },
                    paper: {
                      sx: { minWidth: freightFieldWidthPx },
                    },
                    listbox: {
                      sx: {
                        "& .MuiAutocomplete-option": {
                          whiteSpace: "nowrap",
                        },
                      },
                    },
                  }}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      setFieldValue("freightType", newValue.value);
                    }
                  }}
                  value={selectedFreightOption}
                  sx={{
                    width: freightFieldWidthPx,
                    minWidth: freightFieldWidthPx,
                    flex: "0 0 auto",
                    display: "inline-flex",
                    "& .MuiFormControl-root": {
                      width: freightFieldWidthPx,
                    },
                    "& .MuiAutocomplete-inputRoot": {
                      flexWrap: "nowrap",
                      paddingRight: "28px !important",
                      paddingLeft: 0,
                    },
                    "& .MuiAutocomplete-endAdornment": {
                      right: 0,
                    },
                    "& .MuiAutocomplete-input": {
                      width: `${freightInputWidthPx}px !important`,
                      minWidth: `${freightInputWidthPx}px !important`,
                      flexGrow: 0,
                      overflow: "visible",
                      textOverflow: "clip",
                    },
                  }}
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      label={messages.freightTypeLabel}
                      variant="standard"
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.freightType}
                      helperText={errors.freightType}
                    />
                  )}
                />

                <MDButton
                  variant="outlined"
                  color="secondary"
                  size="small"
                  circular
                  iconOnly
                  onClick={handleOpen}
                  sx={{
                    flexShrink: 0,
                    mb: errors.freightType ? 3 : 0.5,
                  }}
                >
                  <QuestionMarkIcon fontSize="small" />
                </MDButton>

                {isAutomatedFreight && platformHint && (
                  <MDTypography
                    component="span"
                    fontWeight="regular"
                    fontStyle="italic"
                    color={platformHintColor}
                    sx={{
                      fontSize: "0.8125rem",
                      lineHeight: 1.25,
                      whiteSpace: "nowrap",
                      flex: "0 0 auto",
                      flexShrink: 0,
                      pb: errors.freightType ? 3 : 0.5,
                    }}
                  >
                    {platformHint}
                  </MDTypography>
                )}
              </MDBox>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <MDBox sx={{ width: "100%" }}>
                <MDInput
                  name="unitDeliveryTime"
                  label={messages.unitDeliveryTimeLabel}
                  variant="standard"
                  value={values.unitDeliveryTime ?? ""}
                  onChange={(event) => setFieldValue("unitDeliveryTime", event.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: UNIT_DELIVERY_TIME_MAX_LENGTH }}
                  error={!!errors.unitDeliveryTime}
                  helperText={errors.unitDeliveryTime}
                />
                {!errors.unitDeliveryTime && (
                  <FormHelperText
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mt: 0.25,
                      mb: 0,
                      color: "text.secondary",
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                    {messages.unitDeliveryTimeHint}
                  </FormHelperText>
                )}
              </MDBox>
            </Grid>

            <Grid size={12}>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid size={{ xs: 12, md: 6 }}>
                  <MDInput
                    name="freightOverhead"
                    label={messages.freightOverheadLabel}
                    variant="standard"
                    fullWidth
                    value={
                      isPercentageOverhead
                        ? String(values.freightOverhead ?? "")
                        : Number(values.freightOverhead ?? 0).toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })
                    }
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      if (isPercentageOverhead) {
                        const sanitized = event.target.value
                          .replace(/[^\d.,]/g, "")
                          .replace(",", ".");
                        setFieldValue("freightOverhead", sanitized);
                        return;
                      }

                      const digits = event.target.value.replace(/\D/g, "");
                      const numeric = parseInt(digits, 10) || 0;
                      setFieldValue("freightOverhead", (numeric / 100).toString());
                    }}
                    InputLabelProps={{ shrink: true }}
                    InputProps={isPercentageOverhead ? { endAdornment: "%" } : undefined}
                    error={!!errors.freightOverhead}
                    helperText={errors.freightOverhead}
                  />
                </Grid>


                <Grid size={{ xs: 12, md: 6 }}>
                  <MDInput
                    select
                    name="freightOverheadType"
                    label={messages.freightOverheadTypeLabel}
                    variant="standard"
                    fullWidth
                    value={values.freightOverheadType ?? ""}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setFieldValue("freightOverheadType", event.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                  >
                    {freightOverheadTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </MDInput>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <FreightDescriptionModal isOpen={isVisible} onClose={handleClose} messages={messages} />
    </Card>
  );
}

export default FreightCard;
